import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Permutations,
  Survey,
  UserInteractionStatsPayload,
} from '../../interfaces/black-box.interface';
import { Criteria, Quotas } from '../../interfaces/survey.interface';
import { BlackBoxService } from '../../services/pure-price/black-box.service';
import {
  BasicPurePriceQuota,
  QuotaPayload,
  UserInteractionStatsQualPayload,
} from '../../interfaces/quota.interface';
import { QuotaPurePrice } from '../../../create-surveys/pure-price-window/pure-price.interface';
import { QUALIFICATION_NAMES } from '../../../create-surveys/constants/qualification-name';
import { HttpClient } from '@angular/common/http';
import { EnvironmentConfig } from '../../../shared/interfaces/environment.interface';
import { QuotaHelper } from '../../domain/quota-helper';

@Injectable({
  providedIn: 'root',
})
export class PurePriceService {
  private _priceRecoAlreadyApplied: boolean = false;
  private readonly _baseUrl = this._env.environment.pureSpectrum.url;

  constructor(
    private readonly _blackBoxService: BlackBoxService,
    private readonly _http: HttpClient,
    @Inject('ENV') private _env: EnvironmentConfig
  ) {}

  private _mapQuotas(quotas: QuotaPayload[]): QuotaPurePrice[] {
    const nestedQualificationsIds: Array<number> = [];
    let purePriceQuotas: QuotaPurePrice[] = [];
    const nestedQuotas: QuotaPayload[] = [];
    const defaultQuotas: QuotaPayload[] = [];
    for (const quota of quotas) {
      if (quota.quota_category === 'autoNested') {
        for (const criteria of quota.criteria) {
          if (!nestedQualificationsIds.includes(criteria.qualification_code)) {
            nestedQualificationsIds.push(criteria.qualification_code);
          }
        }
        nestedQuotas.push(quota);
      } else if (
        quota.criteria[0].qualification_code &&
        !nestedQualificationsIds.includes(quota.criteria[0].qualification_code)
      ) {
        defaultQuotas.push(quota);
      }
    }
    purePriceQuotas = purePriceQuotas.concat(
      this._mapNestedQuotas(nestedQuotas)
    );
    purePriceQuotas = purePriceQuotas.concat(
      this._mapNormalQuotas(defaultQuotas)
    );
    return purePriceQuotas;
  }

  private _mapNestedQuotas(quotas: QuotaPayload[]) {
    const quotaCodes = [...new Set(quotas.map((q) => q.quota_category || ''))];
    return quotaCodes.map((code: string) => {
      const quota = quotas.find((q) => q.quota_category === code)!;
      const { quota_title: title } = quota;
      const name = QUALIFICATION_NAMES.NESTED;
      const criterias = quotas
        .filter((qc) => qc.quota_category === code)
        .map((qc) => {
          return this._mapCriteria(qc);
        });

      return {
        code,
        name: name || title || '',
        cpiAvg: 0,
        goalTotal: 0,
        total: 0,
        criterias,
        permutations: [],
      };
    });
  }

  private _mapNormalQuotas(quotas: QuotaPayload[]) {
    const quotaCodes = [
      ...new Set(
        quotas.map((q) => q.criteria[0].qualification_code.toString())
      ),
    ];
    return quotaCodes.map((code: string) => {
      const quota = quotas.find(
        (q) => q.criteria[0].qualification_code.toString() === code
      )!;
      const criteria = quota.criteria[0];
      const { quota_title: title } = quota;
      const { qualification_name: name } = criteria;
      const criterias = quotas
        .filter((qc) => qc.criteria[0].qualification_code.toString() === code)
        .map((qc) => {
          return this._mapCriteria(qc);
        });

      return {
        code,
        name: name || title || '',
        cpiAvg: 0,
        goalTotal: 0,
        total: 0,
        criterias,
        permutations: [],
      };
    });
  }

  private _mapCriteria(qc: QuotaPayload) {
    const { buyer_quota_id: id } = qc;
    const description =
      qc.quota_title ||
      new QuotaHelper().breakArrayAndJoinQuotaViewItem(qc.criteria);
    return {
      id,
      description,
      cpi: 0,
      goals: qc.required_count,
      price: 0,
      permutations: [],
      payload: qc,
    };
  }

  getQuotas(survey: Survey, quotas: QuotaPayload[]) {
    if (!quotas.length) {
      //This validation should be before call the function
      return of([]);
    }
    const quotasPureprice = this._mapQuotas(quotas);
    const requests: Observable<void>[] = [];
    quotasPureprice.forEach((q) => {
      q.criterias.forEach((c) => {
        const request = this._blackBoxService.generateBlackBoxPayload(
          this._quotaPayloadToQuota(c.payload),
          survey
        );

        const recommendedRequest = this._blackBoxService
          .getRecommendedCpi(request)
          .pipe(
            map(({ price_recommendation, loss_ratio, EPC }) => {
              c.cpi = price_recommendation;
              c.loss_ratio = loss_ratio;
              c.EPC = EPC;
              c.price = c.cpi * c.goals;
            })
          );
        const permutationRequest = this._blackBoxService
          .calculatePermutations(request)
          .pipe(
            map(({ permutations }: Permutations) => {
              c.permutations = permutations;
            })
          );
        requests.push(...[recommendedRequest, permutationRequest]);
      });
    });

    return forkJoin(requests).pipe(
      map(() => this._finalizeQuotaCalculations(quotasPureprice))
    );
  }

  private _finalizeQuotaCalculations(quotas: QuotaPurePrice[]) {
    quotas.forEach((q) => {
      // create average cpi permutations
      // sum
      q.criterias.forEach((c) => {
        c.permutations.forEach((p, i) => {
          if (!q.permutations[i]) {
            q.permutations.push({ ...p });
          } else {
            // NOTE: we assume the serve always send ordered by field_time
            q.permutations[i].sup_cpi += p.sup_cpi;
          }
        });
      });
      // average
      q.permutations.forEach((p) => {
        p.sup_cpi = p.sup_cpi / q.criterias.length;
      });

      // totals
      q.cpiAvg = this._getCpiAvg(q);
      q.goalTotal = this._getGoalTotal(q);
      q.total = this._getTotal(q);
    });

    return quotas;
  }

  /**
   *
   * Convert a QuotaInterface/QuotaPayload object into a SurveyInterface/Quota
   * object
   */
  private _quotaPayloadToQuota(quotaPayload: QuotaPayload): Quotas {
    const quotas = {} as Quotas;
    const quotaCriteria: Criteria[] = [];
    quotaPayload.criteria.forEach((c) => {
      const criteriaElm: Criteria = {
        qualification_code: c.qualification_code,
        condition_codes: c.condition_codes || [],
        qualification_name: c.qualification_name || '',
        condition_names: c.condition_names || [],
        range_sets: [],
      };

      if (c.range_sets) {
        c.range_sets.forEach((rs) => {
          criteriaElm.range_sets.push({
            from: rs.from || 0,
            to: rs.to || 0,
            units: rs.units || 0,
          });
        });
      }

      quotaCriteria.push(criteriaElm);
    });
    quotas.criteria = quotaCriteria;
    return quotas;
  }

  private _sumQuotasProp(
    quota: QuotaPurePrice,
    prop: 'price' | 'cpi' | 'goals'
  ) {
    return quota.criterias.reduce((acc, next) => acc + (next[prop] ?? 0), 0);
  }

  private _getTotal(quota: QuotaPurePrice) {
    return this._sumQuotasProp(quota, 'price');
  }

  private _getCpiAvg(quota: QuotaPurePrice) {
    return this._sumQuotasProp(quota, 'cpi') / quota.criterias.length;
  }

  private _getGoalTotal(quota: QuotaPurePrice) {
    return this._sumQuotasProp(quota, 'goals');
  }

  public setPricingRecoFlyoutOpened(survey_id: number) {
    const payload = { survey_id };
    if (survey_id && !this._priceRecoAlreadyApplied) {
      this._blackBoxService.pricingRecoFlyOutOpened(payload).subscribe(() => {
        this._priceRecoAlreadyApplied = true;
      });
    }
  }

  public pushApplyPriceRecoButtonCount(surveyId: number) {
    return this._http
      .put(
        `${this._baseUrl}/buyers/v2/pricing/notifications/apply-button/${surveyId}`,
        null
      )
      .toPromise();
  }

  public pushApplyPriceRecoButtonSeenCount(surveyId: number) {
    return this._http
      .put(
        `${this._baseUrl}/buyers/v2/pricing/notifications/apply-button-viewed/${surveyId}`,
        null
      )
      .toPromise();
  }

  private _setUserInteractionStats(
    surveyId: number,
    userInteractionStats: UserInteractionStatsPayload
  ) {
    return this._http
      .put(
        `${this._baseUrl}/buyers/v2/pricing/notifications/user-interaction/${surveyId}`,
        userInteractionStats
      )
      .toPromise();
  }

  /**
   * @param LOI length of interview/survey
   * @param IR survey incidence rate
   */
  public setUserInteraction(
    surveyId: number,
    LOI: number,
    IR: number,
    qualificationName: string,
    purePriceQuotaStats: Array<UserInteractionStatsQualPayload>
  ): void {
    const payload = this._buildUserInteractionsStatsPayload(
      qualificationName,
      LOI,
      IR,
      purePriceQuotaStats
    );
    this._setUserInteractionStats(surveyId, payload);
  }

  /**
   * @param qualificationName
   * @param LOI length of interview/survey
   * @param IR survey incidence rate
   * @param qualifications
   * @returns
   */
  private _buildUserInteractionsStatsPayload(
    qualificationName: string,
    LOI: number,
    IR: number,
    qualifications: Array<UserInteractionStatsQualPayload>
  ) {
    const quotas: Array<BasicPurePriceQuota> = [
      {
        q_name: qualificationName.toLowerCase(),
        qualifications,
      },
    ];

    return <UserInteractionStatsPayload>{ LOI, IR, quotas };
  }

  public mapQuotaToUserInteractionStatsQualPayload(quotas: Array<Quotas>) {
    const purePriceQuotasToUpdateStatus: Array<UserInteractionStatsQualPayload> =
      [];
    for (const purePriceQuota of quotas) {
      const loss_ratio = purePriceQuota.loss_ratio!;
      const q_name = purePriceQuota.criteria[0].condition_names[0];
      const price = purePriceQuota.priceRecommendCpi;
      const EPC = purePriceQuota.EPC!;
      purePriceQuotasToUpdateStatus.push({ loss_ratio, q_name, price, EPC });
    }
    return purePriceQuotasToUpdateStatus;
  }
}
